import { MessageA2uiSurfaces } from 'features/chat/components/a2ui/MessageA2uiSurfaces';
import { InterleavedStreamContent } from 'features/chat/components/InterleavedStreamContent';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import { parseMessageTimeForDisplay } from 'features/chat/utils/message-show-time';
import type { MessageA2uiSurface } from 'features/chat/types/chat';
import { Check, Clipboard } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import dark from 'react-syntax-highlighter/dist/esm/styles/prism/atom-dark';
import light from 'react-syntax-highlighter/dist/esm/styles/prism/prism';

type SyntaxHighlighterTheme = Record<string, React.CSSProperties>;

const prismDarkTheme = dark as unknown as SyntaxHighlighterTheme;
const prismLightTheme = light as unknown as SyntaxHighlighterTheme;

type PreviewChatItemProps = {
  name?: string;
  message: string;
  isMarkedDown?: boolean;
  isBot?: boolean;
  timestamp?: string;
  /** Agent name is shown in the widget header bar; hide it on message rows when false. */
  showAgentName?: boolean;
  /** From widget designSettings.chatFontColor when applyWidgetColors is enabled. */
  fontColor?: string;
  a2uiSurfaces?: MessageA2uiSurface[];
  conversationId?: string | null;
  userMessageId?: string;
  assistantMessageId?: string;
  isLatest?: boolean;
};

const previewMessageTextClassName =
  'min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap';
const previewUserBubbleClassName =
  'min-w-0 max-w-[80%] break-words [overflow-wrap:anywhere] rounded-2xl rounded-tr-sm bg-[#fff7b8] px-4 py-2.5 text-sm text-[#171b23]';

const getBotMessageContent = (message: string, isMarkedDown: boolean) => {
  const trimmed = message.trim();

  try {
    const parsed = JSON.parse(trimmed);

    if (typeof parsed === 'string') {
      return { type: 'text' as const, content: parsed };
    }

    if (typeof parsed === 'object' && parsed !== null) {
      const result = (parsed as Record<string, unknown>).result;

      if (typeof result === 'string' && result.trim()) {
        return { type: 'text' as const, content: result };
      }

      const formatted = JSON.stringify(parsed, null, 2);
      return { type: 'json' as const, content: formatted };
    }
  } catch {
    // Not JSON — fall through to markdown/plain text rendering.
  }

  if (isMarkedDown) {
    return { type: 'markdown' as const, content: message };
  }

  return { type: 'text' as const, content: message };
};

function PreviewJsonBlock({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const lines = content.split('\n');

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const highlighterStyle = isDark ? prismDarkTheme : prismLightTheme;

  return (
    <div className="min-w-0 w-full max-w-full rounded-md border bg-muted">
      <div
        className="min-w-0 w-full max-w-full overflow-visible px-3 py-3"
        style={{ background: 'hsl(var(--surface))', borderRadius: '0 0 0.375rem 0.375rem' }}
      >
        {lines.map((line, index) => (
          <div key={`${index}-${line}`} className="flex min-w-0 items-start gap-3">
            <span className="w-5 shrink-0 select-none text-right text-[0.8125rem] leading-[1.5] text-muted-foreground/60">
              {index + 1}
            </span>
            <div className="min-w-0 flex-1 overflow-visible [&_.react-syntax-highlighter]:!m-0 [&_.react-syntax-highlighter]:!overflow-visible [&_.react-syntax-highlighter]:!bg-transparent [&_.react-syntax-highlighter]:!p-0">
              <SyntaxHighlighter
                showLineNumbers={false}
                wrapLongLines
                style={highlighterStyle}
                customStyle={{
                  margin: 0,
                  padding: 0,
                  background: 'transparent',
                  fontSize: '0.8125rem',
                  lineHeight: '1.5',
                  overflow: 'visible',
                  overflowY: 'visible',
                  maxHeight: 'none',
                  height: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
                codeTagProps={{
                  style: {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  },
                }}
                language="json"
                PreTag="div"
              >
                {line.length > 0 ? line : ' '}
              </SyntaxHighlighter>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const formatChatTimestamp = (timestamp?: string) => {
  if (!timestamp) return '';

  const date = parseMessageTimeForDisplay(timestamp);
  if (!date) return '';

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateSeparator = (timestamp?: string) => {
  if (!timestamp) return '';

  const date = parseMessageTimeForDisplay(timestamp);
  if (!date) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
};

export function DateSeparator({ date, fontColor }: { date: string; fontColor?: string }) {
  return (
    <div className="flex items-center gap-3 py-4">
      <div className="h-px flex-1 bg-border" />
      <span
        className={fontColor ? 'text-xs' : 'text-xs text-muted-foreground'}
        style={fontColor ? { color: fontColor, opacity: 0.7 } : undefined}
      >
        {formatDateSeparator(date)}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function PreviewChatItem({
  name,
  message,
  isMarkedDown = false,
  isBot = true,
  timestamp,
  showAgentName = true,
  fontColor,
  a2uiSurfaces,
  conversationId,
  userMessageId,
  assistantMessageId,
  isLatest,
}: PreviewChatItemProps) {
  const textStyle = fontColor ? { color: fontColor } : undefined;
  const mutedTextStyle = fontColor ? { color: fontColor, opacity: 0.7 } : undefined;
  const botMessage = isBot ? getBotMessageContent(message, isMarkedDown) : null;

  if (!isBot) {
    return (
      <div className="flex w-full min-w-0 flex-col items-end gap-1">
        <span
          className={fontColor ? 'text-xs' : 'text-xs text-muted-foreground'}
          style={mutedTextStyle}
        >
          {formatChatTimestamp(timestamp)}
        </span>
        <div
          className={previewUserBubbleClassName}
          style={textStyle}
        >
          <p className={previewMessageTextClassName} style={textStyle}>
            {message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 w-full max-w-full flex-col gap-1" style={textStyle}>
      {timestamp ? (
        <span
          className={fontColor ? 'text-xs' : 'text-xs text-muted-foreground'}
          style={mutedTextStyle}
        >
          {formatChatTimestamp(timestamp)}
        </span>
      ) : null}
      {showAgentName && name ? (
        <h4
          className={fontColor ? 'text-sm font-medium' : 'text-sm font-medium text-foreground'}
          style={textStyle}
        >
          {name}
        </h4>
      ) : null}
      <div
        className={
          botMessage?.type === 'text'
            ? 'min-w-0 w-full py-2.5 text-sm'
            : botMessage?.type === 'json'
              ? 'min-w-0 w-full text-sm'
              : 'min-w-0 w-full text-sm [&_.prose]:max-w-full [&_.prose]:break-words [&_.prose]:[overflow-wrap:anywhere] [&_.prose_*]:text-inherit'
        }
        style={textStyle}
      >
        {botMessage?.type === 'text' ? (
          <>
            <p className={previewMessageTextClassName} style={textStyle}>
              {botMessage.content}
            </p>
            {!!a2uiSurfaces?.length && (
              <MessageA2uiSurfaces
                surfaces={a2uiSurfaces}
                conversationId={conversationId}
                userMessageId={userMessageId}
                assistantMessageId={assistantMessageId}
                isLatest={isLatest}
              />
            )}
          </>
        ) : botMessage?.type === 'json' ? (
          <>
            <PreviewJsonBlock content={botMessage.content} />
            {!!a2uiSurfaces?.length && (
              <MessageA2uiSurfaces
                surfaces={a2uiSurfaces}
                conversationId={conversationId}
                userMessageId={userMessageId}
                assistantMessageId={assistantMessageId}
                isLatest={isLatest}
              />
            )}
          </>
        ) : (
          <InterleavedStreamContent
            content={botMessage?.content ?? message}
            surfaces={a2uiSurfaces}
            renderText={(text) => <MarkdownRenderer content={text} />}
            renderSurfaces={(surfaces) => (
              <MessageA2uiSurfaces
                surfaces={surfaces}
                conversationId={conversationId}
                userMessageId={userMessageId}
                assistantMessageId={assistantMessageId}
                isLatest={isLatest}
              />
            )}
          />
        )}
      </div>
    </div>
  );
}
